import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faInfoCircle,
  faCarrot,
  faTimes,
  faLeaf,
  faDollarSign
} from '@fortawesome/free-solid-svg-icons';
import { getAllToppings } from '../../db';
import { usePriceOverrides } from '../../context/PriceOverridesContext';
import './ToppingSelector.css';

const ToppingSelector = ({ selectedToppings, onUpdateToppings, includedToppings = [] }) => {
  // Subscribe so direct db reads below pick up override changes.
  usePriceOverrides();
  // Get topping options from the database
  const toppingOptions = getAllToppings();
  
  // Track first render
  
  
  // Track extra meat quantity
  const [extraMeatQuantity, setExtraMeatQuantity] = useState(1);
  
  // Store the extra meat quantity in a custom prop on the parent component
  useEffect(() => {
    if (selectedToppings.includes(14)) {
      // Create a custom event to communicate the extra meat quantity
      const event = new CustomEvent('extraMeatQuantityChange', { 
        detail: { quantity: extraMeatQuantity }
      });
      document.dispatchEvent(event);
    }
  }, [selectedToppings, extraMeatQuantity]);
  
  const handleToppingToggle = (toppingId) => {
    // If already selected, remove it regardless of whether it's a style default
    if (selectedToppings.includes(toppingId)) {
      // Just remove this topping without any extra conditions
      const updatedToppings = selectedToppings.filter(id => id !== toppingId);
      onUpdateToppings(updatedToppings); // It's OK if this is empty
      
      // Reset extra meat quantity when removing it
      if (toppingId === 14) {
        setExtraMeatQuantity(1);
      }
    } else {
      // Otherwise add it
      const updatedToppings = [...selectedToppings, toppingId];
      onUpdateToppings(updatedToppings);
    }
  };
  
  const isToppingSelected = (toppingId) => {
    return selectedToppings.includes(toppingId);
  };
  
  const isToppingIncluded = (toppingId) => {
    // Only lettuce is truly included free
    return toppingOptions.find(t => t.id === toppingId)?.isIncluded || false;
  };
  
  const isToppingIncludedWithStyle = (toppingId) => {
    // Check if this topping is included with the selected style
    return includedToppings.includes(toppingId);
  };
  
  const getFreeToppings = () => {
    // Only include toppings that are explicitly marked as free (just lettuce)
    return toppingOptions.filter(topping => topping.price === 0);
  };
  
  const getPremiumToppings = () => {
    // All non-free toppings are premium, even if they're included with a style
    return toppingOptions.filter(topping => topping.price > 0);
  };
  
  const freeToppings = getFreeToppings();
  const premiumToppings = getPremiumToppings();
  
  const formatPrice = (price) => {
    if (price === 0) return "Free";
    return `$${price.toFixed(2)}`;
  };
  
  return (
    <div className="topping-selector-container">
      {/* Free toppings section - only show if there are truly free toppings */}
      {freeToppings.length > 0 && (
        <div className="free-toppings-section">
          <h4 className="toppings-category-title">
            <FontAwesomeIcon icon={faLeaf} />
            Free Toppings
          </h4>
          <div className="topping-grid">
            {freeToppings.map(topping => (
              <div
                key={topping.id}
                className={`topping-card free-topping ${isToppingSelected(topping.id) ? 'selected' : ''}`}
                onClick={() => handleToppingToggle(topping.id)}
              >
                <FontAwesomeIcon icon={faCarrot} className="topping-icon" />
                <span className="topping-name">{topping.name}</span>
                {isToppingSelected(topping.id) && (
                  <FontAwesomeIcon icon={faTimes} className="remove-topping" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Premium toppings section */}
      {premiumToppings.length > 0 && (
        <div className="premium-toppings-section">
          <h4 className="toppings-category-title">
            <FontAwesomeIcon icon={faDollarSign} />
            Premium Toppings
          </h4>
          <div className="topping-grid">
            {premiumToppings.map(topping => 
              topping.id === 14 ? (
                // Special handler for Extra Meat option
                <div
                  key={topping.id}
                  data-topping-id={topping.id}
                  data-quantity="1"
                  className={`topping-card premium-topping ${isToppingSelected(topping.id) ? 'selected' : ''}`}
                  onClick={() => handleToppingToggle(topping.id)}
                >
                  <FontAwesomeIcon icon={faCarrot} className="topping-icon" />
                  <span className="topping-name">{topping.name}</span>
                  <span className="topping-price">
                    +$1.95/oz
                  </span>
                  {isToppingSelected(topping.id) && (
                    <>
                      <FontAwesomeIcon icon={faTimes} className="remove-topping" />
                      <div className="extra-meat-quantity" onClick={(e) => e.stopPropagation()}>
                        <label>
                          <span>Quantity:</span>
                          <select 
                            defaultValue="1"
                            value={extraMeatQuantity}
                            onChange={(e) => {
                              setExtraMeatQuantity(parseInt(e.target.value));
                            }}
                          >
                            <option value="1">1 oz ($1.95)</option>
                            <option value="2">2 oz ($3.90)</option>
                            <option value="3">3 oz ($5.85)</option>
                            <option value="4">4 oz ($7.80)</option>
                            <option value="5">5 oz ($9.75)</option>
                          </select>
                        </label>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                // Regular premium topping
                <div
                  key={topping.id}
                  data-topping-id={topping.id}
                  className={`topping-card premium-topping ${isToppingSelected(topping.id) ? 'selected' : ''}`}
                  onClick={() => handleToppingToggle(topping.id)}
                >
                  <FontAwesomeIcon icon={faCarrot} className="topping-icon" />
                  <span className="topping-name">{topping.name}</span>
                  <span className={`topping-price ${isToppingIncludedWithStyle(topping.id) ? 'included-style-price' : ''}`}>
                    {isToppingIncludedWithStyle(topping.id) 
                      ? "Included with Style" 
                      : (topping.price === 0 ? "Free" : `+${formatPrice(topping.price)}`)}
                  </span>
                  {isToppingSelected(topping.id) && (
                    <FontAwesomeIcon icon={faTimes} className="remove-topping" />
                  )}
                </div>
              )
            )}
          </div>
        </div>
      )}
      
      {/* Summary of selections */}
      {selectedToppings.length > 0 && (
        <div className="topping-summary">
          <h4>Selected Toppings:</h4>
          <div className="selected-toppings-list">
            {selectedToppings.map(toppingId => {
              const topping = toppingOptions.find(t => t.id === toppingId);
              
              return (
                <div 
                  key={toppingId} 
                  className={`selected-topping-pill ${isToppingIncluded(toppingId) ? 'free' : (isToppingIncludedWithStyle(toppingId) ? 'included-style' : 'premium')}`}
                >
                  <span>{topping?.name}</span>
                  {!isToppingIncludedWithStyle(toppingId) && topping?.price > 0 && 
                    <span className="premium-indicator">+${topping.price.toFixed(2)}</span>
                  }
                  {isToppingIncludedWithStyle(toppingId) && 
                    <span className="included-indicator">Included with style</span>
                  }
                  <button 
                    className="remove-topping-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToppingToggle(toppingId);
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <div className="topping-info-note">
        <h4>
          <FontAwesomeIcon icon={faInfoCircle} />
          About Toppings
        </h4>
        <p>
          Premium toppings are available at various price points. Lettuce and onions are free, all other toppings have an additional charge.
          {includedToppings.length > 0 && (
            <>
              <br/><br/>
              <strong>Special pricing note:</strong> Toppings marked as "Included with Style" come with your selected sandwich style. 
              If you remove these toppings, you'll receive a discount equal to their regular price.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default ToppingSelector;